/**
 * Patient dashboard — self-scoped view of visits, care plans, care team & billing.
 * Backend role-filters all list endpoints to the calling patient.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { visitAPI, carePlanAPI, prescriptionAPI, billingAPI, requestAPI } from '../../../services/api';
import Skeleton from '../../../components/common/Skeleton';
import { deriveCareJourney } from '../../clinical/careJourney';
import {
  T, FONT, I, firstNameOf, asList, fmtDateTime, fmtDate, fmtMoney,
  FadeSection, Hero, KpiCard, ChartCard, EmptyState, StatusBadge,
  Avatar, ListRow, QuickActions, PolarStyles,
} from '../shared';

// ── Vertical journey timeline (patient lens) ─────────────────────────────────
function JourneyTimeline({ steps }) {
  return (
    <div style={{ paddingTop: 4 }}>
      {steps.map((s, i) => {
        const last = i === steps.length - 1;
        const isDone = s.state === 'done';
        const isCurrent = s.state === 'current';
        return (
          <div key={s.key} style={{ display: 'flex', gap: 14 }}>
            {/* marker + connector */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 26, flexShrink: 0 }}>
              {isDone ? (
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', background: T.teal,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <I.check size={13} color="#fff" />
                </span>
              ) : isCurrent ? (
                <span className="hhms-journey-pulse" style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: '#fff', border: `3px solid ${T.primary}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary }} />
                </span>
              ) : (
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: '#fff', border: `2px solid ${T.border}`,
                }} />
              )}
              {!last && (
                <span style={{
                  width: 2, flex: 1, minHeight: 22,
                  background: isDone ? T.teal : T.border,
                }} />
              )}
            </div>
            {/* label + caption */}
            <div style={{ paddingBottom: last ? 0 : 18, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: isCurrent ? 800 : 700,
                color: isDone ? T.text : isCurrent ? T.primary : T.muted,
                lineHeight: '24px',
              }}>
                {s.label}
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{s.caption}</div>
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes hhms-journey-ring {
          0%, 100% { box-shadow: 0 0 0 0 ${T.primary}40; }
          50%      { box-shadow: 0 0 0 6px ${T.primary}1A; }
        }
        .hhms-journey-pulse { animation: hhms-journey-ring 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .hhms-journey-pulse { animation: none; } }
      `}</style>
    </div>
  );
}

export default function PatientDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [requests, setRequests] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      visitAPI.list({ limit: 50 }).catch(() => null),
      carePlanAPI.list({ limit: 50 }).catch(() => null),
      prescriptionAPI.list({ limit: 50 }).catch(() => null),
      billingAPI.list({ limit: 50 }).catch(() => null),
      requestAPI.list({ limit: 50 }).catch(() => null),
    ]).then(([v, c, p, b, rq]) => {
      if (v.status === 'fulfilled') setVisits(asList(v.value));
      if (c.status === 'fulfilled') setCarePlans(asList(c.value));
      if (p.status === 'fulfilled') setPrescriptions(asList(p.value));
      if (b.status === 'fulfilled') setInvoices(asList(b.value));
      if (rq.status === 'fulfilled') setRequests(asList(rq.value));
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const upcoming = visits
    .filter(v => {
      const st = String(v.status || '').toUpperCase();
      const start = v.scheduled_start ? new Date(v.scheduled_start) : null;
      return !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(st) && start && start >= now;
    })
    .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  const activePlans = carePlans.filter(c => String(c.status || '').toUpperCase() === 'ACTIVE');
  const activeRx = prescriptions.filter(p => ['ACTIVE', 'ISSUED'].includes(String(p.status || '').toUpperCase()));
  const pendingInvoices = invoices.filter(i =>
    ['PENDING', 'OVERDUE', 'PARTIALLY_PAID'].includes(String(i.payment_status || i.status || '').toUpperCase()));

  // Care team — unique nurse names from visits.
  const careTeam = [...new Set(visits.map(v => v.nurse_name).filter(Boolean))];

  // Care journey — derived from already-fetched data (shared with Patient 360).
  const journey = deriveCareJourney({ requests, carePlans, visits });

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.KPIGrid count={4} cols={4} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const kpis = [
    { title: 'Upcoming Visits', value: upcoming.length, color: T.primary, Icon: I.calendar, sub: upcoming.length ? 'scheduled' : 'none scheduled' },
    { title: 'Active Care Plans', value: activePlans.length, color: T.teal, Icon: I.heart, sub: `${carePlans.length} total` },
    { title: 'Active Prescriptions', value: activeRx.length, color: T.purple, Icon: I.pill, sub: `${prescriptions.length} total` },
    { title: 'Pending Invoices', value: pendingInvoices.length, color: T.warning, Icon: I.coin, sub: pendingInvoices.length ? 'awaiting payment' : 'all settled' },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Patient portal"
        title={`Welcome back, ${firstName} 💙`}
        subtitle="Here's an overview of your care — visits, care plans and billing in one place."
        stats={[
          { label: 'Upcoming visits', value: upcoming.length, Icon: I.calendar },
          { label: 'Care plans', value: activePlans.length, Icon: I.heart },
        ]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      <FadeSection index={2} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
        <ChartCard title="My care journey" subtitle="Where you are in your care, step by step">
          {journey.hasAny ? (
            <JourneyTimeline steps={journey.steps} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <EmptyState
                icon={I.heart}
                color={T.teal}
                title="Your journey starts here"
                caption="Your care journey will appear here once you request your first service"
              />
              <button
                onClick={() => navigate('/care-requests')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                  background: T.primary, color: '#fff', border: 'none',
                  fontSize: 13, fontWeight: 700, fontFamily: FONT,
                }}
              >
                <I.request size={14} color="#fff" /> Request your first service
              </button>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Upcoming Visits" subtitle="Your next scheduled home-care visits">
          {upcoming.length ? upcoming.slice(0, 6).map((v, i) => (
            <ListRow
              key={v.visit_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.primary}12` }}><I.calendar size={16} color={T.primary} /></span>}
              title={fmtDateTime(v.scheduled_start)}
              sub={v.nurse_name ? `Nurse ${v.nurse_name}` : 'Nurse to be assigned'}
              right={<StatusBadge value={v.status} />}
            />
          )) : (
            <EmptyState icon={I.calendar} color={T.primary} title="No upcoming visits yet" caption="When a visit is scheduled for you, it will appear here." />
          )}
        </ChartCard>

        <ChartCard title="My Care Plans" subtitle="Plans your care team is following">
          {carePlans.length ? carePlans.slice(0, 6).map((c, i) => (
            <ListRow
              key={c.care_plan_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.teal}12` }}><I.heart size={16} color={T.teal} /></span>}
              title={c.title || c.plan_name || `Care plan #${c.care_plan_id ?? i + 1}`}
              sub={c.review_date ? `Review: ${fmtDate(c.review_date)}` : (c.start_date ? `Started: ${fmtDate(c.start_date)}` : null)}
              right={<StatusBadge value={c.status} />}
            />
          )) : (
            <EmptyState icon={I.heart} color={T.teal} title="No care plans yet" caption="Your doctor will create a care plan once your care begins." />
          )}
        </ChartCard>

        <ChartCard title="My Care Team" subtitle="Nurses who have cared for you">
          {careTeam.length ? careTeam.slice(0, 6).map((name, i) => (
            <ListRow
              key={name}
              left={<Avatar name={name} index={i} />}
              title={`Nurse ${name}`}
              sub="Home-care nurse"
            />
          )) : (
            <EmptyState icon={I.users} color={T.purple} title="No care team yet" caption="Your assigned nurses will appear here after your first visit." />
          )}
        </ChartCard>

        <ChartCard title="Recent Invoices" subtitle="Your latest billing activity">
          {invoices.length ? invoices.slice(0, 6).map((inv, i) => (
            <ListRow
              key={inv.invoice_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.warning}12` }}><I.coin size={16} color={T.warning} /></span>}
              title={fmtMoney(inv.total_amount ?? inv.amount)}
              sub={inv.invoice_number ? `Invoice ${inv.invoice_number}` : (inv.due_date ? `Due ${fmtDate(inv.due_date)}` : null)}
              right={<StatusBadge value={inv.payment_status || inv.status} />}
            />
          )) : (
            <EmptyState icon={I.coin} color={T.warning} title="No invoices yet" caption="Invoices for your care will appear here once issued." />
          )}
        </ChartCard>
      </FadeSection>

      <FadeSection index={3}>
        <QuickActions
          navigate={navigate}
          actions={[
            { label: 'Request Care', path: '/care-requests', color: T.primary, Icon: I.request },
            { label: 'My Visits',    path: '/visits',        color: T.teal,    Icon: I.calendar },
            { label: 'My Billing',   path: '/billing',       color: T.warning, Icon: I.coin },
            { label: 'My Profile',   path: '/profile',       color: T.purple,  Icon: I.users },
          ]}
        />
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
